import { z } from "zod";
import { diaDesdeIso, esquemaFechaPapel, formatearDia, MS_POR_DIA, sumarDias } from "./fechas.js";

/**
 * Cada cuánto se repite una diligencia (fase 3b, ampliación del 2026-10-20). Hasta aquí toda
 * diligencia era diaria; ahora cada asignación (formato × persona) dice si toca todos los días,
 * un día de la semana, un día del mes o cada N días. La frecuencia va en la **asignación**, no
 * en el formato: el día concreto (los lunes, el 5) es de la persona (dos personas pueden llenar
 * el mismo formato en días distintos) y lo único que la lee es la pantalla "Diligencias" y su
 * contador, que son por persona.
 *
 * Las fechas son días "solo día" (medianoche UTC, como las columnas `date` y `hoyLocal()`).
 */

export const TIPOS_DE_FRECUENCIA = ["diaria", "semanal", "mensual", "cada_n_dias"] as const;
export type TipoDeFrecuencia = (typeof TIPOS_DE_FRECUENCIA)[number];

export const NOMBRE_TIPO_DE_FRECUENCIA: Record<TipoDeFrecuencia, string> = {
  diaria: "Todos los días",
  semanal: "Cada semana",
  mensual: "Cada mes",
  cada_n_dias: "Cada N días",
};

/** Lunes = 1 … domingo = 7, como ISO 8601 y como se dice la semana en la oficina. */
export const DIAS_DE_LA_SEMANA = [
  "lunes",
  "martes",
  "miércoles",
  "jueves",
  "viernes",
  "sábado",
  "domingo",
] as const;

export const esquemaFrecuencia = z.discriminatedUnion("tipo", [
  z.object({ tipo: z.literal("diaria") }),
  z.object({
    tipo: z.literal("semanal"),
    /** 1 = lunes … 7 = domingo. */
    diaSemana: z.number().int().min(1, "Elige el día de la semana").max(7),
  }),
  z.object({
    tipo: z.literal("mensual"),
    /** 1 … 31; en un mes más corto toca el último día. */
    diaMes: z.number().int().min(1, "Elige el día del mes").max(31),
  }),
  z.object({
    tipo: z.literal("cada_n_dias"),
    cadaDias: z.number().int().min(2, "Cada 2 días como mínimo").max(366),
    /** AAAA-MM-DD: el primer día que toca; de ahí se cuenta. */
    desde: esquemaFechaPapel("Escribe desde qué día se cuenta"),
  }),
]);
export type Frecuencia = z.infer<typeof esquemaFrecuencia>;

export const FRECUENCIA_DIARIA: Frecuencia = { tipo: "diaria" };

/** El día de la semana (1 = lunes … 7 = domingo) de una fecha "solo día". */
export function diaDeLaSemana(dia: Date): number {
  return ((dia.getUTCDay() + 6) % 7) + 1;
}

function ultimoDiaDelMes(dia: Date): number {
  return new Date(Date.UTC(dia.getUTCFullYear(), dia.getUTCMonth() + 1, 0)).getUTCDate();
}

/** ¿Toca llenar la diligencia este día? */
export function tocaElDia(f: Frecuencia, dia: Date): boolean {
  switch (f.tipo) {
    case "diaria":
      return true;
    case "semanal":
      return diaDeLaSemana(dia) === f.diaSemana;
    case "mensual":
      return dia.getUTCDate() === Math.min(f.diaMes, ultimoDiaDelMes(dia));
    case "cada_n_dias": {
      const dias = Math.round((dia.getTime() - diaDesdeIso(f.desde).getTime()) / MS_POR_DIA);
      return dias >= 0 && dias % f.cadaDias === 0;
    }
  }
}

/** El primer día, desde `desde` inclusive, en que toca. */
export function proximoDia(f: Frecuencia, desde: Date): Date {
  if (f.tipo === "cada_n_dias") {
    const inicio = diaDesdeIso(f.desde);
    if (inicio.getTime() >= desde.getTime()) return inicio;
  }
  // Como mucho un mes (31 días) o el paso de "cada N días".
  const tope = f.tipo === "cada_n_dias" ? f.cadaDias : 31;
  for (let i = 0; i <= tope; i++) {
    const dia = sumarDias(desde, i);
    if (tocaElDia(f, dia)) return dia;
  }
  return desde;
}

/** "Todos los días" · "Cada lunes" · "El día 5 de cada mes" · "Cada 3 días desde el 20 oct 2026". */
export function describirFrecuencia(f: Frecuencia): string {
  switch (f.tipo) {
    case "diaria":
      return "Todos los días";
    case "semanal":
      return `Cada ${DIAS_DE_LA_SEMANA[f.diaSemana - 1] ?? "semana"}`;
    case "mensual":
      return f.diaMes === 31 ? "El último día de cada mes" : `El día ${f.diaMes} de cada mes`;
    case "cada_n_dias":
      return `Cada ${f.cadaDias} días desde el ${formatearDia(diaDesdeIso(f.desde))}`;
  }
}

export function mismaFrecuencia(a: Frecuencia, b: Frecuencia): boolean {
  return JSON.stringify(frecuenciaAColumnas(a)) === JSON.stringify(frecuenciaAColumnas(b));
}

/** Cómo se guarda en la tabla `diligencias`: un tipo y, según el tipo, una de las otras columnas. */
type ColumnasDeFrecuencia = {
  frecuencia: string;
  diaSemana: number | null;
  diaMes: number | null;
  cadaDias: number | null;
  desde: Date | null;
};

export function frecuenciaAColumnas(f: Frecuencia): ColumnasDeFrecuencia {
  return {
    frecuencia: f.tipo,
    diaSemana: f.tipo === "semanal" ? f.diaSemana : null,
    diaMes: f.tipo === "mensual" ? f.diaMes : null,
    cadaDias: f.tipo === "cada_n_dias" ? f.cadaDias : null,
    desde: f.tipo === "cada_n_dias" ? diaDesdeIso(f.desde) : null,
  };
}

/** La frecuencia que dicen las columnas; una fila incompleta o desconocida cuenta como diaria. */
export function frecuenciaDeColumnas(c: ColumnasDeFrecuencia): Frecuencia {
  switch (c.frecuencia) {
    case "semanal":
      return c.diaSemana ? { tipo: "semanal", diaSemana: c.diaSemana } : FRECUENCIA_DIARIA;
    case "mensual":
      return c.diaMes ? { tipo: "mensual", diaMes: c.diaMes } : FRECUENCIA_DIARIA;
    case "cada_n_dias":
      return c.cadaDias && c.desde
        ? { tipo: "cada_n_dias", cadaDias: c.cadaDias, desde: c.desde.toISOString().slice(0, 10) }
        : FRECUENCIA_DIARIA;
    default:
      return FRECUENCIA_DIARIA;
  }
}
